import type { ReactNode } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';

interface CollapsibleSectionProps {
  title: string;
  defaultExpanded?: boolean;
  actions?: ReactNode;
  children: ReactNode;
  /** Called when expanded state changes. */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * When false, the section is NOT collapsible: the disclosure chevron and
   * whole-row toggle are dropped and the children are always rendered. Used on
   * the full-page CRM record where hiding the overview serves no purpose.
   * @default true
   */
  collapsible?: boolean;
  /**
   * A one-line précis of what the collapsed panel is hiding, rendered
   * immediately after the title and hidden again once the section opens
   * (the panel's own content takes over from there).
   *
   * A collapsed section is otherwise indistinguishable from an empty one:
   * "Research" over three completed briefs and "Research" over nothing at
   * all are the same row. Pass whatever answers "is there anything in
   * here, and what state is it in" — a status pill, a count, a timestamp.
   * Pass nothing when there is genuinely nothing to report; an always-on
   * "0 items" is noise, not a signal.
   *
   * Non-interactive: it sits under the whole-row toggle, so a summary
   * containing a button or link would be unclickable. Use `actions` for
   * anything the user is meant to press.
   *
   * Ignored when `collapsible` is false — nothing is hidden there, so
   * there is nothing to preview.
   */
  summary?: ReactNode;
  /**
   * Extra content rendered at the far right of the header row, after
   * `actions` (e.g. a panel-collapse control). Always interactive.
   */
  headerTrailing?: ReactNode;
  /**
   * Replaces the whole header row — title, actions, trailing, chevron and
   * the whole-row toggle — while set. The overview swaps in a search field
   * when every value is shown; whatever the override renders is the way
   * back. The disclosure keeps its state meanwhile (react-aria's Disclosure
   * doesn't need its trigger mounted), so the panel stays open underneath.
   */
  headerOverride?: ReactNode;
}

/**
 * Section accordion used throughout the entity slideout (overview, notes,
 * people, timeline, research, meetings…). Composes the shared
 * `<AccordionItem>` for smooth animation + react-aria a11y, with the
 * slideout's specific layout conventions:
 *
 *  - Whole row toggles on click (via overlay-trigger pattern).
 *  - Actions (e.g. "+ Add note") sit inline at the right end of the row,
 *    are real <button>s, and DO NOT toggle the disclosure when clicked
 *    (they re-enable `pointer-events-auto` over the overlay).
 *  - Compact `py-2` trigger padding; no horizontal padding (the slideout
 *    container provides it).
 *  - Chevron points down when collapsed and rotates up when expanded
 *    (the design-system default `AccordionChevron` variant).
 *  - A hover background on the whole row, because the chevron alone is
 *    too quiet to compete with a brand-coloured `actions` button.
 *  - An optional `summary` beside the title, shown only while collapsed,
 *    so a closed section isn't indistinguishable from an empty one.
 */
export function CollapsibleSection({
  title,
  defaultExpanded = true,
  actions,
  children,
  onExpandedChange,
  collapsible = true,
  summary,
  headerTrailing,
  headerOverride,
}: CollapsibleSectionProps) {
  // Non-collapsible: plain header row (title + actions + trailing) with the
  // children always shown — no disclosure chevron, no whole-row toggle.
  if (!collapsible) {
    return (
      <div>
        {/* Non-collapsible header (full-page record's details rail). No TOP
            padding and a `pb-2.5` bottom so this row's content centers at the
            same height as the main column's tab bar (which also has no top
            padding + `pb-2.5`), keeping the "{Type} overview" heading vertically
            aligned with the record's tab/name heading row across the page. The
            36px trailing controls (Edit / collapse) set the row height, matching
            the tab bar's tabs/avatar — pinned as `min-h-[46px]` (36 + pb-2.5)
            so the row keeps that height when the override (a 32px search
            field) stands in for them, and the rail below doesn't move. */}
        <div className='flex min-h-[46px] items-center gap-2 pb-2.5'>
          {headerOverride ?? (
            <>
              <span className='flex-1 min-w-0 text-xs md:text-sm font-medium text-[var(--color-text-primary)]'>
                {title}
              </span>
              {actions && <div className='shrink-0'>{actions}</div>}
              {headerTrailing && (
                <div className='shrink-0'>{headerTrailing}</div>
              )}
            </>
          )}
        </div>
        {children}
      </div>
    );
  }

  return (
    <AccordionItem
      defaultExpanded={defaultExpanded}
      onExpandedChange={onExpandedChange}
    >
      {headerOverride ? (
        // The override owns the row outright: no whole-row toggle beneath
        // it to catch clicks meant for its controls. `min-h-9` is the title
        // row's height (py-2 around a 20px line), so a 32px control swapped
        // in here doesn't move the panel below by a pixel.
        <div className='flex min-h-9 items-center gap-2 animate-in fade-in duration-150 motion-reduce:animate-none'>
          {headerOverride}
        </div>
      ) : (
        // Overlay-trigger pattern: invisible AccordionTrigger fills the
        // row, visible content sits in a sibling layer with
        // `pointer-events-none`, and the actions buttons re-enable
        // `pointer-events-auto` so their clicks don't toggle the
        // disclosure.
        <div className='relative'>
          {/* The trigger is absolutely positioned BEFORE the visible layer in
              DOM order, so its hover background paints behind the row's
              content rather than over it. That background is the row's only
              cue that it is pressable at all: the chevron alone loses the
              eye to a brand-coloured `actions` button sitting right beside
              it. `actions` keeps its own (tertiary) hover, so hovering the
              button still reads as distinct from hovering the row. */}
          <AccordionTrigger
            aria-label={title}
            className='absolute inset-0 w-full rounded transition-colors hover:bg-[var(--color-bg-secondary)] focus-visible:ring-inset'
          >
            {null}
          </AccordionTrigger>

          <div className='pointer-events-none relative flex items-center gap-2 py-2'>
            <div className='flex flex-1 min-w-0 items-center gap-2'>
              <span className='min-w-0 text-xs md:text-sm font-medium text-[var(--color-text-primary)]'>
                {title}
              </span>
              {summary && (
                // Collapsed-only: once the panel is open its own content is
                // the better answer, and a duplicated status pill directly
                // above the real one just reads as a rendering bug.
                // `group/item` + `data-expanded` come from <AccordionItem>.
                <div className='flex min-w-0 items-center gap-2 group-data-[expanded]/item:hidden'>
                  {summary}
                </div>
              )}
            </div>
            {actions && (
              <div className='pointer-events-auto shrink-0'>{actions}</div>
            )}
            {headerTrailing && (
              <div className='pointer-events-auto shrink-0'>
                {headerTrailing}
              </div>
            )}
            <AccordionChevron
              variant='default'
              className='w-5 h-5 text-fg-secondary'
            />
          </div>
        </div>
      )}
      <AccordionPanel>{children}</AccordionPanel>
    </AccordionItem>
  );
}
